import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { LoginService } from '@ebw-monorepo/login';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { MtxGrid, MtxGridColumn, MtxGridRowClassFormatter } from '@ng-matero/extensions/grid';
import { TranslateService } from '@ngx-translate/core';
import { IMapTable } from 'libs/ng-components/map/src/lib/interfaces/mapTable.interface';
import { FeatureSelectService } from 'libs/ng-components/map/src/lib/services/feature-select.service';
import { MapTableService } from 'libs/ng-components/map/src/lib/services/map-table.service';
import { MapService } from 'libs/ng-components/map/src/lib/services/map.service';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import { safeJsonStringify } from '@ebw-monorepo/microservices-helpers/utils/parser';
import moment from 'moment';
import { GeoJSON } from 'ol/format';
import { Subject, Subscription, debounceTime, filter, lastValueFrom, switchMap, takeUntil, tap } from 'rxjs';
import { SnackbarService } from '../../../../services/snackbar.service';
import { ToOpenHistoryModalComponent } from '../to-open-history/to-open-history-modal.component';

enum SensibilitaGiunto {
  NonSensibile = '',
  LavorazioniNotturne = 'Lavorazioni Notturne',
  Sensibile = 'Sensibile'
}

@Component({
  selector: 'ebw-monorepo-checkin-analysis-table',
  templateUrl: './checkin-analysis-table.component.html',
  styleUrls: ['./checkin-analysis-table.component.scss']
})
export class CheckinAnalysisTableComponent implements OnInit, OnDestroy {
  @ViewChild('grid') public gridTable: MtxGrid;

  @ViewChild('actionsColTemplate', { static: true })
  actionsColTemplate!: TemplateRef<any>;

  @ViewChild('toOpenColTemplate', { static: true })
  toOpenColTemplate!: TemplateRef<any>;

  @ViewChild('exportColTemplate', { static: true })
  exportColTemplate!: TemplateRef<any>;

  // --------------------------------------------------------------------------------------------------------------------

  public dataGrid = [];
  public columnsGrid: MtxGridColumn[] = [];
  public innerGridColumns: MtxGridColumn[] = [];
  public isOwner = false;
  public pageSize = 10;
  public pageSizeOptions = [10, 20, 50];
  public currentPageIndex = 0;
  public offset = 0;
  public totalCount = 0;
  public config: IMapTable = null;
  public searchFilter: FormGroup;
  public searchFilterOpen = false;
  private _destroy$ = new Subject<void>();
  private _currentSort: { field: string; direction: number };
  private _exportApi;
  private _filterSubscribe: Subscription;

  public rowClassFormatter: MtxGridRowClassFormatter = {
    highlighted: (data, index) => {
      return !data.to_open;
    }
  };

  // --------------------------------------------------------------------------------------------------------------------

  constructor(
    private mapTableSrv: MapTableService,
    private mapService: MapService,
    private ebwModal: EbwModalService,
    private _dialog: MatDialog,
    private _translateService: TranslateService,
    private _utilsService: UtilsService,
    private _featureSelectService: FeatureSelectService,
    private _mapService: MapService,
    private _snackbarService: SnackbarService,
    private _fb: FormBuilder,
    private apiUrlService: ApiUrlService
  ) {}

  // --------------------------------------------------------------------------------------------------------------------

  ngOnInit(): void {
    this._exportApi = {
      csv: `${this.apiUrlService.createUrl(API_URLS.MAP.EXPORT.CSV)}`,
      html: `${this.apiUrlService.createUrl(API_URLS.MAP.EXPORT.HTML)}`
    };

    this.mapTableSrv
      .getMapTableConfig()
      .pipe(
        takeUntil(this._destroy$),
        filter(Boolean),
        switchMap((config) => {
          const url = this.apiUrlService.createUrl(API_URLS.USER.COMPANY.GET_USER_COMPANY);
          this.config = config;
          this._currentSort = null;
          return this._utilsService.getRequest(`${url}?user_id=${sessionStorage.getItem(LoginService.USER_ID_KEY)}`);
        }),
        tap((resp: any) => {
          this.isOwner = resp.owner;
        }),
        switchMap(() => this._fetchData())
      )
      .subscribe({
        next: (resp: any) => {
          this.initializeTable();
        },
        error: (err) => {
          console.error(err);
          this.ebwModal.openErrorModal({
            title: this._translateService.instant('common.error'),
            content: 'Errore recupero elementi'
          });
          this.initializeTable();
        }
      });

    this._initFilterForm();
  }

  // --------------------------------------------------------------------------------------------------------------------

  ngOnDestroy() {
    this.closeMapTable();
    this._destroy$.next();
    if (this._filterSubscribe) {
      this._filterSubscribe.unsubscribe();
    }
  }

  // --------------------------------------------------------------------------------------------------------------------

  closeMapTable() {
    this.mapTableSrv.setMapTableConfig(null);
    this.mapService.clearAllFeaturesFromTemporaryLayer();
    this.mapService.clearAllFeaturesFromTemporaryLayer(true);
  }

  // --------------------------------------------------------------------------------------------------------------------
  public async onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPageIndex = event.pageIndex;
    this.offset = event.pageIndex * this.pageSize;

    await lastValueFrom(this._fetchData());
  }

  public async onSortChange(event: Sort) {
    if (event.direction !== '') {
      this._currentSort = {
        field: event.active,
        direction: event.direction === 'asc' ? -1 : 1
      };
    } else {
      this._currentSort = null;
    }

    await lastValueFrom(this._fetchData());
  }

  private _fetchData() {
    if (!this.config) {
      return;
    }

    this.resetComponentProperties();

    let url = this.config?.url;
    let customFilter = {};

    if (this.searchFilter && this.searchFilter.dirty) {
      let rawVal = this.searchFilter.getRawValue();
      customFilter = {
        status: rawVal.status
      };
    }

    let filters = { ...this.getFilters(), ...customFilter };

    return this._utilsService.postRequest(url, filters, false, {}, 'application/json').pipe(
      tap((resp: any) => {
        this.totalCount = resp.numRows;
        this.dataGrid = resp.records;
      })
    );
  }

  // ------------------------------------------------------------------------------------------------------------

  initializeTable() {
    this.columnsGrid = [
      {
        header: '',
        field: 'expand',
        pinned: 'left',
        class: 'px-1',
        showExpand: true
      },
      {
        header: this._translateService.instant(`map.sidebar.checkin_analysis.table.segment_name`),
        field: 'segment_name',
        sortable: true
      },
      {
        header: this._translateService.instant(`map.sidebar.checkin_analysis.table.segment_rc`),
        field: 'segment_rc',
        sortable: true
      },
      {
        header: this._translateService.instant(`map.sidebar.checkin_analysis.table.circuit_name`),
        field: 'circuit_name',
        sortable: true
      },
      {
        header: 'Giunti totali', //this._translateService.instant('map.sidebar.checkin_analysis.table.segment_rc'),
        field: 'giunti_totali',
        class: ''
      },
      {
        header: 'Giunti completati', //this._translateService.instant('map.sidebar.checkin_analysis.table.segment_rc'),
        field: 'giunti_completati'
      },
      {
        header: '',
        field: 'export',
        cellTemplate: this.exportColTemplate
      }
    ];

    // Si occupa di renderizzare il semaforo
    // - se to_open è false è grigio
    // - altrimenti si basa sulla proprietà relativa
    const createCheckInPresenceIcon = (prop: boolean, toOpen: boolean) => {
      if (!toOpen) {
        return `<span class="traffic-light traffic-light--gray"></span>`;
      }
      return `<span class="traffic-light ${prop ? 'traffic-light--green' : 'traffic-light--red'}"></span>`;
    };

    this.innerGridColumns = [
      {
        header: 'Azioni',
        field: 'id',
        width: '50px',
        cellTemplate: this.actionsColTemplate
      },
      {
        header: 'Da aprire',
        field: 'to_open',
        width: '50px',
        cellTemplate: this.toOpenColTemplate
      },
      {
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.check_in_splice_presence'),
        field: 'check_in_splice_presence',
        formatter: (data: any) => createCheckInPresenceIcon(data.check_in_splice_presence, data.to_open)
      },
      {
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.check_in_sheath_in_presence'),
        field: 'check_in_sheath_in_presence',
        formatter: (data: any) => createCheckInPresenceIcon(data.check_in_sheath_in_presence, data.to_open)
      },
      {
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.check_in_sheath_out_presence'),
        field: 'check_in_sheath_out_presence',
        formatter: (data: any) => createCheckInPresenceIcon(data.check_in_sheath_out_presence, data.to_open)
      },
      {
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.date_time_write'),
        field: 'date_time_write',
        formatter: (data: any) => `<span class="date-field">${this._isDateSet(data.date_time_write) ? data.date_time_write : '-'}</span>`
      },
      {
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.date_time_in_service'),
        field: 'date_time_in_service',
        formatter: (data: any) =>
          `<span class="date-field">${
            this._isDateSet(data.date_time_in_service) ? moment(data.date_time_in_service, 'DD/MM/YYYY HH:mm:ss').format('DD/MM/YYYY') : '-'
          }</span>`
      },
      {
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.info_fibra_in'),
        field: 'info_fibra_in'
      },
      {
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.splice_closure_name'),
        field: 'splice_closure_name',
        formatter: (data: any) =>
          `<div class="d-flex flex-column nowrap">
            <span>${data.splice_closure_name}</span><span class="sensibile-${data.sensibilita.replace(' ', '-').toLowerCase()}">${
            data.sensibilita
          }</span>
          </div>`
      },
      {
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.info_fibra_out'),
        field: 'info_fibra_out'
      }
    ];

    if (this.isOwner) {
      this.innerGridColumns.push({
        header: this._translateService.instant('map.sidebar.checkin_analysis.table.system'),
        field: 'system'
      });
    }

    this.gridTable.detectChanges();
  }

  async clearDefaultSearchFilter() {
    this.searchFilter.reset();
    await lastValueFrom(this._fetchData());
  }

  // ------------------------------------------------------------------------------------------------------------

  getFilters(limit = this.pageSize, offset = this.offset) {
    let nextFilters = {
      limit,
      offset
    };

    if (this._currentSort) {
      nextFilters['order_by'] = this._currentSort.field;
      nextFilters['order'] = this._currentSort.direction;
    }

    //Pagination is always applied
    const filters = this.config?.filters;

    //Apply filter
    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) {
          return;
        }
        nextFilters[key] = filters[key];
      });
    }

    return nextFilters;
  }

  // ------------------------------------------------------------------------------------------------------------

  resetComponentProperties() {
    this.dataGrid = [];
    this.mapService.clearAllFeaturesFromTemporaryLayer();
  }

  // ------------------------------------------------------------------------------------------------------------

  async getFeatureInfo(urn: string) {
    if (!urn) {
      const title = this._translateService.instant('common.error');
      const content = 'Urn non trovato';
      this.ebwModal.openErrorModal({ title, content });
      return;
    }
    // get the URL of the selectByUrns endpoint
    const selectByUrns = `${this.apiUrlService.createUrl(API_URLS.MAP.CORE.SELECT_BY_URNS)}`;

    // make an HTTP POST request to the selectByUrns Url passing the urn of the current feature
    this._utilsService.postRequest(selectByUrns, { urns: urn }, false).subscribe({
      next: (response) => {
        // extract the feature
        const feature = new GeoJSON().readFeatures(response.result)[0];

        const { collection_name, collection_external_name } = this._featureSelectService.getConfigDataFromUrn('' + feature.getId());
        feature['urn'] = urn;
        feature['collectionName_'] = collection_name;
        feature['collectionExternalName_'] = collection_external_name;
        feature['id'] = (feature.getId() as string).split('V')[4].split('Z')[1];

        this._featureSelectService.mapFeatureToModal(feature, 'view');
      },
      error: (error) => {
        console.error('[checkin-analysis] - [getFeatureInfo] - error', error);
        // catch any error and display an error message
        this.ebwModal.openErrorModal({
          title: this._translateService.instant('common.error'),
          content: this._translateService.instant('common.data_retrieving_error')
        });
      }
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  async onExport(type: string) {
    const exportData = this.dataGrid.map((el) => el.elements).flat();
    this.exportFile(exportData, type);
  }

  // ------------------------------------------------------------------------------------------------------------

  async onRowExport(row: any, type: string) {
    this.exportFile(row.elements, type);
  }

  // ------------------------------------------------------------------------------------------------------------

  async exportFile(exportData: any[], type: string) {
    let url: string;
    let body: any;
    let blobType: any;

    try {
      switch (type) {
        case 'csv':
          url = this._exportApi.csv ? this._exportApi.csv + '?filename=' + 'checkin-analysis-export' : null;
          body = { data: this._formatExportData(exportData) };
          blobType = 'text/csv';
          break;

        case 'html':
          url = this._exportApi.html ? this._exportApi.html + '?filename=' + 'checkin-analysis-export' : null;
          body = { data: this._formatExportData(exportData) };
          blobType = 'text/html';
          break;
      }

      const opt = {
        responseType: 'blob'
      };

      this._utilsService.postRequest(url, body, false, opt, 'application/json').subscribe({
        next: (data: any) => {
          let downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(new Blob([data], { type: blobType }));
          downloadLink.setAttribute('download', 'checkin-analysis-export');
          document.body.appendChild(downloadLink);
          downloadLink.click();
        },
        error: (error: any) => {
          this.ebwModal.openErrorModal({
            title: this._translateService.instant('common.error'),
            content: safeJsonStringify(error, String(error))
          });
        }
      });
    } catch (error) {
      this.ebwModal.openErrorModal({
        title: this._translateService.instant('common.error'),
        content: safeJsonStringify(error?.message || this._translateService.instant('common.data_retrieving_error'), '')
      });
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  private _formatExportData(data: any[], excel: boolean = false) {
    const formattedData = [];

    const exportColumns = this.innerGridColumns.filter((el) => el.field != 'id');
    // Aggiungo campi che in tabella non si vedono
    exportColumns.unshift({
      header: 'Nome segmento',
      field: 'segment_name'
    });

    exportColumns.unshift({
      header: 'RC',
      field: 'segment_rc'
    });

    for (const row of data) {
      const currentRow = {};
      for (const col of exportColumns) {
        if (typeof row[col.field] === 'object' && !Array.isArray(row[col.field]) && row[col.field] !== null) {
          currentRow[<string>col.header] = `${row[col.field].latitude} ${row[col.field].longitude}`;
          continue;
        }

        // Gestisco i booleani
        if (
          col.field === 'check_in_splice_presence' ||
          col.field === 'check_in_sheath_in_presence' ||
          col.field === 'check_in_sheath_out_presence' ||
          col.field === 'to_open'
        ) {
          currentRow[<string>col.header] = row[col.field].toString();
          continue;
        }

        if (!row[col.field]) {
          currentRow[<string>col.header] = '';
          continue;
        }

        currentRow[<string>col.header] = row[col.field];
      }
      formattedData.push(currentRow);
    }

    if (excel) {
      return {
        query: formattedData
      };
    }

    return formattedData;
  }

  // ------------------------------------------------------------------------------------------------------------

  async goToFromSpliceClosure(urn: string) {
    try {
      this._mapService.clearAllFeaturesFromTemporaryLayer(true);
      const response = await this._featureSelectService.selectByUrns([urn]);
      let feature = response?.result?.features[0];
      let struttura = feature.properties?.ebw_struttura[0];
      let geometry = feature.geometry;

      if (!struttura && !feature.geometry) {
        this._snackbarService.open(`Nessuna geometria per l'oggetto selezionato`);
        return;
      }

      if (struttura) {
        const objStr = JSON.parse(struttura);
        const responseStrutura = await this._featureSelectService.selectByUrns([objStr.urn]);
        let featureStruttura = responseStrutura?.result?.features[0];
        geometry = featureStruttura.geometry;
      }

      // if (!featureStruttura.geometry) {
      //   const geometry = await this._featureSelectService.getPlacedAssetGeometry(featureStruttura);
      //   featureStruttura.geometry = geometry;
      // }

      if (geometry) {
        geometry.coordinates = this._mapService.transformGeometryCordsFrom4326To3857(geometry.coordinates);
        //centro la mappa sulla geometry
        this._mapService.centerMapOnGeometry(geometry);
        this._mapService.addFeatureToTemporaryLayer(geometry, null, true);
        return;
      }

      this._snackbarService.open(`Nessuna geometria per l'oggetto selezionato`);
    } catch (error) {
      console.error('[goToFromSpliceClosure][goToFromSpliceClosure]', error);
    }
  }

  // ------------------------------------------------------------------------------------------------------------

  private _isDateSet(dateSrt: string) {
    return !!dateSrt && dateSrt !== 'unset';
  }

  // ------------------------------------------------------------------------------------------------------------

  private _initFilterForm() {
    this.searchFilter = this._fb.group({
      status: [0]
    });
    this._filterSubscribe = this.searchFilter.valueChanges.pipe(debounceTime(300)).subscribe(async (val) => {
      await lastValueFrom(this._fetchData());
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  getStoricoToOpen(data: any) {
    this._dialog.open(ToOpenHistoryModalComponent, {
      maxWidth: '800px',
      minWidth: '500px',
      width: '50%',
      maxHeight: '75vh',
      data: { giunto: data }
    });
  }
}
